import React, {useCallback} from 'react';
import styled from 'styled-components';
import {VideoInfo} from 'shared/components/VideoPlayer/entity/VideoInfo';
import {Thumbnail} from 'shared/components/VideoPlayer/Thumbnail';

interface PlaylistProps {
    videos: VideoInfo[];
    watched: Record<string, boolean>;
    selectedVideo: VideoInfo;
    playVideo: (video: VideoInfo) => void;
}

export const Playlist = ({
    videos,
    watched,
    selectedVideo,
    playVideo,
}: PlaylistProps) => {
    const handleClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            const id = Number(event.currentTarget.dataset.videoId);
            const video = videos.find((item) => item.id === id);

            if (video) playVideo(video);
        },
        [videos, playVideo]
    );

    return (
        <List>
            {videos.map((video, index) => {
                const active = video.id === selectedVideo.id;
                const isWatched = !!watched[video.watchedKey];

                return (
                    <li key={video.id}>
                        <PlaylistItem
                            type="button"
                            $active={active}
                            aria-current={active ? 'true' : undefined}
                            data-video-id={video.id}
                            onClick={active ? undefined : handleClick}>
                            <PlaylistThumb>
                                <Thumbnail
                                    videoInfo={video}
                                    watched={isWatched ? !active : null}
                                    compact
                                />
                            </PlaylistThumb>
                            <div>
                                <PlaylistTitle>
                                    {index + 1}. {video.title}
                                </PlaylistTitle>
                                {active ? (
                                    <PlaylistStatus>Now playing</PlaylistStatus>
                                ) : isWatched ? (
                                    <PlaylistStatus>Watched</PlaylistStatus>
                                ) : null}
                            </div>
                        </PlaylistItem>
                    </li>
                );
            })}
        </List>
    );
};

const List = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const PlaylistItem = styled.button<{$active: boolean}>`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 6px;
    border: 0;
    border-radius: 10px;
    text-align: left;
    cursor: ${({$active}) => ($active ? 'default' : 'pointer')};
    background: ${({$active}) => ($active ? '#e3effb' : 'transparent')};

    &:hover {
        background: #e3effb;
    }
`;

const PlaylistThumb = styled.div`
    width: 104px;
    flex-shrink: 0;
`;

const PlaylistTitle = styled.div`
    font-weight: 600;
    font-size: 0.9em;
    line-height: 1.3;
    color: ${({theme}) => theme.colors.primary.main};
`;

const PlaylistStatus = styled.div`
    font-size: 0.8em;
    color: #60788e;
`;
